import { useMutation } from '@tanstack/react-query';
import { ExternalLink, Loader2 } from 'lucide-react';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useLinkedCaseUrl } from '@/hooks/useLinkedCaseUrl';
import { type MedBVProgramOption, useMedBVPrograms } from '@/hooks/useMedBVPrograms';
import { caseRecordService } from '@/services/api';

interface MoveToMedBVProps {
  caseId: string;
  linkedCaseRecordIds?: string[];
  organizationId: string;
  programId: string;
  /**
   * Callback when migration is successful. Receives the new case record ID.
   */
  onMigrationSuccess?: (newCaseRecordId: string) => void;
}

/**
 * Component that renders either:
 * - A dropdown to migrate a medicare_human case to a medbv workflow (if no linked cases)
 * - A link to the linked case (if linkedCaseRecordIds exists)
 */
export const MoveToMedBV: React.FC<MoveToMedBVProps> = ({
  caseId,
  linkedCaseRecordIds,
  organizationId,
  programId,
  onMigrationSuccess,
}) => {
  const { medBVProgramOptions, isLoading: isLoadingPrograms } = useMedBVPrograms();
  const [pendingProgram, setPendingProgram] = useState<MedBVProgramOption | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Get the first linked case ID if it exists (hook must be called unconditionally)
  const linkedCaseId = linkedCaseRecordIds?.[0];
  const { url: linkedCaseUrl, isLoading: isLoadingUrl } = useLinkedCaseUrl(linkedCaseId);

  const migrateMutation = useMutation({
    mutationFn: (targetWorkflowId: string) =>
      caseRecordService.migrateCaseRecordToMedBv({
        organization_id: organizationId,
        program_id: programId,
        case_record_id: caseId,
        target_workflow_id: targetWorkflowId,
      }),
    onSuccess: data => {
      toast.success('Case migrated to MedBV successfully');

      if (data.new_case_record_id && onMigrationSuccess) {
        onMigrationSuccess(data.new_case_record_id);
      }

      // Reload page to show the linked case ID
      window.location.reload();
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to migrate case to MedBV');
    },
  });

  const handleProgramSelect = (programId: string) => {
    const selectedOption = medBVProgramOptions.find(opt => opt.program.id === programId);
    if (selectedOption?.medbvWorkflow.id) {
      setPendingProgram(selectedOption);
      setShowConfirmDialog(true);
    }
  };

  const handleConfirmMigration = () => {
    if (pendingProgram?.medbvWorkflow.id) {
      migrateMutation.mutate(pendingProgram.medbvWorkflow.id);
    }
    setShowConfirmDialog(false);
    setPendingProgram(null);
  };

  const handleCancelMigration = () => {
    setShowConfirmDialog(false);
    setPendingProgram(null);
  };

  // If there are linked case records, show a link to the first one
  if (linkedCaseId) {
    // Show loading state while resolving the URL
    if (isLoadingUrl) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          <span>Linked: {linkedCaseId.slice(-8)}</span>
        </span>
      );
    }

    // If URL couldn't be resolved, show static text
    if (!linkedCaseUrl) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <span>Linked: {linkedCaseId.slice(-8)}</span>
        </span>
      );
    }

    // Show the direct link
    return (
      <Link
        to={linkedCaseUrl}
        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
      >
        <ExternalLink className="h-3 w-3" />
        <span>Linked: {linkedCaseId.slice(-8)}</span>
      </Link>
    );
  }

  // Don't render anything if no medbv programs are available
  if (!isLoadingPrograms && medBVProgramOptions.length === 0) {
    return null;
  }

  const isDisabled = migrateMutation.isPending || isLoadingPrograms;

  return (
    <>
      <Select onValueChange={handleProgramSelect} disabled={isDisabled}>
        <SelectTrigger className="h-5 w-[140px] text-xs px-2">
          {migrateMutation.isPending || isLoadingPrograms ? (
            <div className="flex items-center gap-1">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>{migrateMutation.isPending ? 'Migrating...' : 'Loading...'}</span>
            </div>
          ) : (
            <SelectValue placeholder="Move to MedBV" />
          )}
        </SelectTrigger>
        <SelectContent>
          {medBVProgramOptions.map(option => (
            <SelectItem key={option.program.id} value={option.program.id || ''}>
              {option.program.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Dialog open={showConfirmDialog} onOpenChange={handleCancelMigration}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle>Confirm Migration</DialogTitle>
            <DialogDescription>
              Are you sure you want to move this case to MedBV program &quot;
              {pendingProgram?.program.name}&quot;? This action will create a new linked case
              record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelMigration}>
              Cancel
            </Button>
            <Button onClick={handleConfirmMigration}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
